import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import type { WebsiteField } from '@/types/blueprint.ts';
import { Info } from 'lucide-react';
import React from 'react';
import { Clear } from './Clear.tsx';

enum FieldStatus {
  Pending = 'pending',
  Completed = 'completed',
  NeedUserReview = 'need_user_review',
}

interface SingleFieldEditorProps {
  categoryId: string;
  field: WebsiteField;
  fieldStatus: FieldStatus;
  isAvailable: boolean;
  notes: string;
  onChange: (params: {
    categoryId: string;
    fieldId: string;
    value: string;
  }) => void;
  onNotesChange: (params: {
    categoryId: string;
    fieldId: string;
    notes: string;
  }) => void;
  onStatusChange: (params: {
    categoryId: string;
    fieldId: string;
    status: FieldStatus;
  }) => void;
  reasoning?: string;
  value: string;
}

const quickFills = ['No Input Required', 'Not provided', 'Carrier Default'];

const SingleFieldEditor: React.FC<SingleFieldEditorProps> = ({
  categoryId,
  field,
  fieldStatus,
  isAvailable,
  notes,
  onChange,
  onNotesChange,
  onStatusChange,
  reasoning,
  value,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ categoryId, fieldId: field.field_id, value: e.target.value });
  };

  const handleQuickFill = (suggestion: string) => {
    onChange({ categoryId, fieldId: field.field_id, value: suggestion });
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onNotesChange({
      categoryId,
      fieldId: field.field_id,
      notes: e.target.value,
    });
  };

  const handleClear = () => {
    onChange({ categoryId, fieldId: field.field_id, value: '' });
  };

  const handleClearNotes = () => {
    onNotesChange({ categoryId, fieldId: field.field_id, notes: '' });
  };

  const handleStatusChange = (newStatus: FieldStatus) => {
    onStatusChange({
      categoryId,
      fieldId: field.field_id,
      status: newStatus,
    });
  };

  const getStatusColor = (status: FieldStatus) => {
    switch (status) {
      case FieldStatus.Completed:
        return 'bg-green-100 text-green-700';
      case FieldStatus.NeedUserReview:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getStatusText = (status: FieldStatus) => {
    switch (status) {
      case FieldStatus.Completed:
        return 'Completed';
      case FieldStatus.NeedUserReview:
        return 'Need Review';
      default:
        return 'Pending';
    }
  };

  return (
    <div className={`flex flex-col gap-1 border-b border-gray-200 pb-1 ${!isAvailable ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label
            className="text-base font-medium"
            htmlFor={`field-${categoryId}-${field.field_id}`}
          >
            {field.field_name}
          </label>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${
            !isAvailable 
              ? 'bg-gray-100 text-gray-500' 
              : 'bg-red-100 text-red-700'
          }`}>
            {!isAvailable ? 'Not Needed' : 'Required'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {fieldStatus === FieldStatus.Pending ? (
            // When pending, show two action buttons
            <>
              <Button
                className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleStatusChange(FieldStatus.Completed)}
                size="sm"
                type="button"
              >
                ✓ Mark Complete
              </Button>
              <Button
                className="h-7 px-3 text-xs bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => handleStatusChange(FieldStatus.NeedUserReview)}
                size="sm"
                type="button"
              >
                ⚠ Need Review
              </Button>
            </>
          ) : (
            // When already moved forward, show current status and one action button
            <>
              <span className={`text-xs font-semibold px-2 py-1 rounded ${getStatusColor(fieldStatus)}`}>
                {getStatusText(fieldStatus)}
              </span>
              {fieldStatus === FieldStatus.Completed ? (
                <Button
                  className="h-7 px-3 text-xs bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => handleStatusChange(FieldStatus.NeedUserReview)}
                  size="sm"
                  type="button"
                >
                  ⚠ Need Review
                </Button>
              ) : (
                <Button
                  className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleStatusChange(FieldStatus.Completed)}
                  size="sm"
                  type="button"
                >
                  ✓ Mark Complete
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mb-1 text-xs italic text-gray-500 flex items-center justify-between">
        <p>
        {field.additional_info ||
          'The date when the insurance coverage should start'}
        </p>
                    {reasoning && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="h-6 w-6 p-1" size="xs" variant="ghost">
                  <Info className="text-gray-500" size={12} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="bg-white max-w-xs rounded-md p-2 text-sm">{reasoning}</p>
              </TooltipContent>
            </Tooltip>
          )}
      </div>
      <Input
        className="rounded-lg border border-gray-300 bg-gray-50 px-4 py-4 text-base focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
        id={`field-${categoryId}-${field.field_id}`}
        onChange={handleInputChange}
        placeholder={`Enter ${field.field_name.toLowerCase()}...`}
        value={value}
      />
      <div className="mt-1 flex items-center gap-2">
        <Label className="text-xs font-medium">Quick Fill:</Label>
        {quickFills.map((suggestion) => (
          <Button
            className="border-gray-300 px-2 py-1 text-xs italic text-gray-700"
            key={suggestion}
            onClick={() => handleQuickFill(suggestion)}
            size="xs"
            type="button"
            variant="outline"
          >
            {suggestion}
          </Button>
        ))}
        {value && <Clear onClick={handleClear} />}
      </div>

      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="mb-2 block text-sm font-medium text-gray-700">
            Instructions/Notes
          </Label>
          {notes && <Clear onClick={handleClearNotes} />}
        </div>
        <Textarea
          className="min-h-[60px] border border-gray-300 bg-gray-50 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
          onChange={handleNotesChange}
          placeholder="Add any instructions or notes for this field..."
          value={notes}
        />
      </div>
    </div>
  );
};

export default SingleFieldEditor;
